import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Compass, Video, Store, BookOpen, Wand2, LogOut, Bot, Crown, Coins, BarChart3, Radio, PlayCircle } from "lucide-react";
import AIAssistantDock from "@/components/AIAssistantDock";
import NotificationsBell from "@/components/NotificationsBell";
import TourPlayer from "@/components/TourPlayer";
import usePresence from "@/lib/usePresence";

const ZONES = [
  { to: "/social", label: "Social", icon: Compass, testid: "nav-social" },
  { to: "/creator", label: "Creator", icon: Video, testid: "nav-creator" },
  { to: "/marketplace", label: "Marketplace", icon: Store, testid: "nav-marketplace" },
  { to: "/knowledge", label: "Knowledge", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/studio", label: "AI Studio", icon: Wand2, testid: "nav-studio" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const { active, connected } = usePresence();

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top nav */}
      <header className="sticky top-0 z-50 glass-heavy">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-3 flex items-center gap-4">
          <NavLink to="/social" data-testid="logo-link" className="flex items-center gap-2 mr-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center glow-ring">
              <Sparkles size={18} className="text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">NEXUS</span>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 ml-2">
            {ZONES.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    isActive
                      ? "glass-active text-[#00E5FF]"
                      : "text-[#64748B] hover:text-[#0F172A] hover:bg-slate-100"
                  }`
                }
              >
                <Icon size={16} />
                <span className="font-medium">{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <div
              data-testid="presence-indicator"
              className="hidden sm:flex items-center gap-1.5 text-xs glass px-3 py-1.5 rounded-full"
              title={connected ? "Live" : "Reconnecting"}
            >
              <Radio size={12} className={connected ? "text-[#00E5FF]" : "text-[#64748B]"} />
              <span className="text-[#0F172A] font-bold">{active}</span>
              <span className="text-[#64748B]">live</span>
            </div>
            <button
              data-testid="open-tour"
              onClick={() => setTourOpen(true)}
              className="hidden sm:flex items-center gap-2 text-xs px-3 py-2 rounded-full glass hover:text-[#1877F2] transition"
              title="Replay product tour"
            >
              <PlayCircle size={14} /> <span className="hidden lg:inline">Tour</span>
            </button>
            <button
              data-testid="open-ai-assistant"
              onClick={() => setAssistantOpen(true)}
              className="nexus-btn-secondary flex items-center gap-2 text-sm py-2 px-4"
            >
              <Bot size={16} /> <span className="hidden sm:inline">AI Assistant</span>
            </button>
            <NavLink
              to="/analytics"
              data-testid="nav-analytics"
              className={({ isActive }) =>
                `w-9 h-9 rounded-full glass flex items-center justify-center ${isActive ? "text-[#7B2CBF]" : "hover:text-[#7B2CBF]"}`
              }
              title="Analytics"
            >
              <BarChart3 size={16} />
            </NavLink>
            <NotificationsBell />
            <NavLink
              to="/earnings"
              data-testid="nav-earnings"
              className={({ isActive }) =>
                `w-9 h-9 rounded-full glass flex items-center justify-center ${isActive ? "text-[#00E5FF]" : "hover:text-[#00E5FF]"}`
              }
              title="Earnings"
            >
              <Coins size={16} />
            </NavLink>
            <NavLink
              to="/profile"
              data-testid="nav-profile"
              className={({ isActive }) =>
                `w-9 h-9 rounded-full glass flex items-center justify-center ${isActive ? "text-[#F5C542]" : "hover:text-[#F5C542]"}`
              }
              title="Your empire"
            >
              <Crown size={16} />
            </NavLink>
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-xs text-[#64748B]">Operator</span>
              <span data-testid="current-user-name" className="text-sm font-semibold text-[#0F172A] truncate max-w-[160px]">
                {user?.name}
              </span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="w-9 h-9 rounded-full glass flex items-center justify-center hover:text-[#F5C542]"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
          {ZONES.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${
                  isActive ? "glass-active text-[#00E5FF]" : "text-[#64748B]"
                }`
              }
            >
              <Icon size={14} /> {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-[1500px] w-full mx-auto px-4 sm:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      </main>

      <AnimatePresence>
        {assistantOpen && <AIAssistantDock onClose={() => setAssistantOpen(false)} />}
        {tourOpen && <TourPlayer variant="modal" onClose={() => setTourOpen(false)} />}
      </AnimatePresence>
    </div>
  );
}
